import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock,
  Send,
  CheckCircle2,
  Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Section, FadeIn } from '../components/Section';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const projectTypes = {
  en: ['Show Fountain', 'Dry Deck Fountain', 'Urban Plaza Fountain', 'Hotel / Resort Fountain', 'Landmark Fountain', 'Interactive Fountain', 'Renovation Project', 'Technical Consultation', 'Other'],
  fr: ['Fontaine Spectacle', 'Fontaine Sèche', 'Fontaine Place Urbaine', 'Fontaine Hôtel / Resort', 'Fontaine Monument', 'Fontaine Interactive', 'Projet de Rénovation', 'Consultation Technique', 'Autre'],
  es: ['Fuente Espectáculo', 'Fuente Seca', 'Fuente Plaza Urbana', 'Fuente Hotel / Resort', 'Fuente Monumento', 'Fuente Interactiva', 'Proyecto de Renovación', 'Consulta Técnica', 'Otro'],
};

const regions = {
  en: ['Europe', 'North Africa', 'Africa', 'Mediterranean', 'Middle East', 'Gulf Region', 'International'],
  fr: ['Europe', 'Afrique du Nord', 'Afrique', 'Méditerranée', 'Moyen-Orient', 'Région du Golfe', 'International'],
  es: ['Europa', 'Norte de África', 'África', 'Mediterráneo', 'Oriente Medio', 'Región del Golfo', 'Internacional'],
};

export default function Contact() {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    projectType: '',
    region: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const currentProjectTypes = projectTypes[language] || projectTypes.en;
  const currentRegions = regions[language] || regions.en;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setIsSubmitted(true);
        toast.success(language === 'fr' ? 'Message envoyé avec succès !' : language === 'es' ? '¡Mensaje enviado con éxito!' : 'Message sent successfully!');
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          projectType: '',
          region: '',
          message: '',
        });
      } else {
        toast.error(language === 'fr' ? 'Échec de l\'envoi. Veuillez réessayer.' : language === 'es' ? 'Error al enviar. Por favor, inténtelo de nuevo.' : 'Failed to send message. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(language === 'fr' ? 'Échec de l\'envoi. Veuillez réessayer.' : language === 'es' ? 'Error al enviar. Por favor, inténtelo de nuevo.' : 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main data-testid="contact-page">
      <SEO page="contact" />
      {/* Hero Section */}
      <section className="relative py-24 lg:py-32 hero-gradient overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-10 right-20 w-80 h-80 rounded-full bg-[#29ABE2]/5 blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <span className="text-sm font-medium uppercase tracking-widest text-[#29ABE2] mb-4 block">
              {t('contact.eyebrow')}
            </span>
            <h1 className="text-5xl lg:text-6xl font-bold text-[#1A1A1A] tracking-tight uppercase mb-6">
              {t('contact.title')}
            </h1>
            <p className="text-xl text-[#6C757D] leading-relaxed">
              {t('contact.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <Section background="white">
        <div className="grid lg:grid-cols-12 gap-16">
          {/* Contact Form */}
          <div className="lg:col-span-7">
            <FadeIn>
              <h2 className="text-2xl font-bold text-[#1A1A1A] mb-6 uppercase tracking-wide">
                {t('contact.form.title')}
              </h2>
              
              {isSubmitted ? (
                <div className="bg-[#F8F9FA] p-8 text-center">
                  <CheckCircle2 className="w-16 h-16 text-[#29ABE2] mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-[#1A1A1A] mb-2">{t('contact.form.success')}</h3>
                  <p className="text-[#6C757D] mb-6">
                    {t('contact.form.successMessage')}
                  </p>
                  <Button
                    onClick={() => setIsSubmitted(false)}
                    variant="outline"
                    className="border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white rounded-none"
                  >
                    {t('contact.form.sendAnother')}
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                        {t('contact.form.name')} *
                      </label>
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        data-testid="contact-name-input"
                        className="input-focus bg-[#F8F9FA] border-gray-200 rounded-none h-12"
                        placeholder={language === 'fr' ? 'Votre nom' : language === 'es' ? 'Su nombre' : 'Your name'}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                        {t('contact.form.email')} *
                      </label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        data-testid="contact-email-input"
                        className="input-focus bg-[#F8F9FA] border-gray-200 rounded-none h-12"
                        placeholder={language === 'fr' ? 'votre@email.com' : language === 'es' ? 'su@email.com' : 'your@email.com'}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                        {t('contact.form.company')}
                      </label>
                      <Input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        data-testid="contact-company-input"
                        className="input-focus bg-[#F8F9FA] border-gray-200 rounded-none h-12"
                        placeholder={language === 'fr' ? 'Votre entreprise' : language === 'es' ? 'Su empresa' : 'Your company'}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                        {t('contact.form.phone')}
                      </label>
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        data-testid="contact-phone-input"
                        className="input-focus bg-[#F8F9FA] border-gray-200 rounded-none h-12"
                        placeholder="+1 234 567 890"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                        {t('contact.form.projectType')}
                      </label>
                      <select
                        name="projectType"
                        value={formData.projectType}
                        onChange={handleChange}
                        data-testid="contact-project-type-select"
                        className="w-full h-12 px-4 bg-[#F8F9FA] border border-gray-200 text-[#1A1A1A] focus:border-[#29ABE2] focus:ring-1 focus:ring-[#29ABE2] outline-none"
                      >
                        <option value="">{t('contact.form.selectProjectType')}</option>
                        {currentProjectTypes.map((type) => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                        {t('contact.form.region')}
                      </label>
                      <select
                        name="region"
                        value={formData.region}
                        onChange={handleChange}
                        data-testid="contact-region-select"
                        className="w-full h-12 px-4 bg-[#F8F9FA] border border-gray-200 text-[#1A1A1A] focus:border-[#29ABE2] focus:ring-1 focus:ring-[#29ABE2] outline-none"
                      >
                        <option value="">{t('contact.form.selectRegion')}</option>
                        {currentRegions.map((region) => (
                          <option key={region} value={region}>{region}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                      {t('contact.form.message')} *
                    </label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      data-testid="contact-message-textarea"
                      className="input-focus bg-[#F8F9FA] border-gray-200 rounded-none resize-none"
                      placeholder={t('contact.form.messagePlaceholder')}
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="contact-submit-btn"
                    className="bg-[#1A1A1A] text-white hover:bg-[#29ABE2] rounded-none px-8 py-4 text-sm font-bold uppercase tracking-wider w-full md:w-auto disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                        {t('contact.form.sending')}
                      </>
                    ) : (
                      <>
                        {t('contact.form.submit')}
                        <Send className="ml-2 w-4 h-4" />
                      </>
                    )}
                  </Button>
                </form>
              )}
            </FadeIn>
          </div>

          {/* Contact Info */}
          <div className="lg:col-span-5">
            <FadeIn delay={0.2}>
              <div className="bg-[#1A1A1A] p-8 mb-8">
                <h3 className="text-xl font-bold text-white mb-6 uppercase tracking-wide">
                  {t('contact.directContact')}
                </h3>
                <div className="space-y-6">
                  <a
                    href="https://wa.me/34607798985"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-whatsapp-link"
                    className="flex items-center gap-4 text-gray-300 hover:text-[#25D366] transition-colors"
                  >
                    <div className="w-12 h-12 bg-[#25D366]/20 rounded-full flex items-center justify-center">
                      <svg viewBox="0 0 24 24" className="w-6 h-6 fill-[#25D366]">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">WhatsApp</p>
                      <p className="text-lg font-medium">+34 607 798 985</p>
                    </div>
                  </a>

                  <a
                    href="tel:+34919933440"
                    className="flex items-center gap-4 text-gray-300 hover:text-[#29ABE2] transition-colors"
                  >
                    <div className="w-12 h-12 bg-[#29ABE2]/20 rounded-full flex items-center justify-center">
                      <Phone className="w-6 h-6 text-[#29ABE2]" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">{language === 'fr' ? 'Téléphone' : language === 'es' ? 'Teléfono' : 'Phone'}</p>
                      <p className="text-lg font-medium">+34 919 933 440</p>
                    </div>
                  </a>

                  <a
                    href="mailto:info@magnumfountain.com"
                    className="flex items-center gap-4 text-gray-300 hover:text-[#29ABE2] transition-colors"
                  >
                    <div className="w-12 h-12 bg-[#29ABE2]/20 rounded-full flex items-center justify-center">
                      <Mail className="w-6 h-6 text-[#29ABE2]" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Email</p>
                      <p className="text-lg font-medium">info@magnumfountain.com</p>
                    </div>
                  </a>
                </div>
              </div>

              <div className="bg-[#F8F9FA] p-8">
                <h3 className="text-lg font-bold text-[#1A1A1A] mb-4 uppercase tracking-wide">
                  {t('contact.areasServed')}
                </h3>
                <div className="flex flex-wrap gap-2 mb-6">
                  {currentRegions.map((region) => (
                    <span
                      key={region}
                      className="px-3 py-1 text-xs bg-white border border-gray-200 text-[#6C757D]"
                    >
                      {region}
                    </span>
                  ))}
                </div>
                <div className="flex items-start gap-3 text-sm text-[#6C757D]">
                  <MapPin className="w-4 h-4 mt-0.5 text-[#29ABE2] flex-shrink-0" />
                  <p>{language === 'fr' ? 'Siège à Rivas Vaciamadrid, Madrid, Espagne — au service de projets internationaux dans le monde entier.' : language === 'es' ? 'Sede en Rivas Vaciamadrid, Madrid, España — sirviendo proyectos internacionales en todo el mundo.' : 'Headquarters in Rivas Vaciamadrid, Madrid, Spain — serving international projects worldwide.'}</p>
                </div>
              </div>

              <div className="mt-8 p-6 border-l-4 border-[#29ABE2] bg-[#F8F9FA]">
                <h4 className="font-bold text-[#1A1A1A] mb-2">{t('contact.ourOffice')}</h4>
                <p className="text-sm text-[#6C757D] mb-4">
                  Calle la Fundicion 4 Bis, Nave 64<br />
                  28529 Rivas Vaciamadrid<br />
                  Madrid, Spain
                </p>
                <h4 className="font-bold text-[#1A1A1A] mb-2">{t('contact.internationalClients')}</h4>
                <p className="text-sm text-[#6C757D]">
                  {t('contact.internationalClientsDesc')}
                </p>
              </div>
            </FadeIn>
          </div>
        </div>
      </Section>

      {/* Google Maps Section */}
      <Section background="white" paddingY="py-12 lg:py-16">
        <FadeIn>
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-bold text-[#1A1A1A] mb-6 uppercase tracking-wide text-center" data-testid="map-title">
              {language === 'fr' ? 'Notre Localisation' : language === 'es' ? 'Nuestra Ubicación' : 'Our Location'}
            </h2>
            <div className="aspect-[16/9] w-full border border-gray-200" data-testid="google-map-embed">
              <iframe
                title="Magnum Fountain SL Office Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3042.4!2d-3.5241!3d40.3399!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd4228a1e9b7b8b7%3A0x0!2sCalle%20de%20la%20Fundici%C3%B3n%204%20Bis%2C%2028529%20Rivas-Vaciamadrid%2C%20Madrid%2C%20Spain!5e0!3m2!1sen!2ses!4v1700000000000"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            <p className="text-sm text-[#6C757D] mt-4 text-center">
              Calle la Fundicion 4 Bis, Nave 64 — 28529 Rivas Vaciamadrid, Madrid, Spain
            </p>
          </div>
        </FadeIn>
      </Section>

      {/* Reassurance Section */}
      <Section background="surface" paddingY="py-16 lg:py-20">
        <div className="grid md:grid-cols-3 gap-8 text-center">
          <FadeIn delay={0.1}>
            <div className="p-6">
              <Clock className="w-10 h-10 text-[#29ABE2] mx-auto mb-4" />
              <h3 className="font-bold text-[#1A1A1A] mb-2">{t('contact.quickResponse')}</h3>
              <p className="text-sm text-[#6C757D]">
                {t('contact.quickResponseDesc')}
              </p>
            </div>
          </FadeIn>
          <FadeIn delay={0.2}>
            <div className="p-6">
              <CheckCircle2 className="w-10 h-10 text-[#29ABE2] mx-auto mb-4" />
              <h3 className="font-bold text-[#1A1A1A] mb-2">{t('contact.noObligation')}</h3>
              <p className="text-sm text-[#6C757D]">
                {t('contact.noObligationDesc')}
              </p>
            </div>
          </FadeIn>
          <FadeIn delay={0.3}>
            <div className="p-6">
              <MapPin className="w-10 h-10 text-[#29ABE2] mx-auto mb-4" />
              <h3 className="font-bold text-[#1A1A1A] mb-2">{t('contact.globalReach')}</h3>
              <p className="text-sm text-[#6C757D]">
                {t('contact.globalReachDesc')}
              </p>
            </div>
          </FadeIn>
        </div>
      </Section>
    </main>
  );
}
